import {isEmpty, pipe} from 'remeda'
import {ForecastGroupKey, ResolutionForecasts, UseForecastGroup, Forecast as F} from '../forecast'
import {Comparison, QuestionColor, QuestionResultsQuestion} from '../question'
import {SubqFetched} from '../subq'
import {ResultsQuery} from './QuestionResults'
import {fmtDate} from '../../format'
import {DateTime} from 'luxon'
import {doSafe} from '../../promise'
import {isNullOrEmpty} from '../../collection'
import {notNull} from '../../type'
import {Subqs} from '../subqs'
import {ForecastFetchedBase} from '../forecastTypes'
import {match} from 'ts-pattern'

export type QRComparison = Pick<Comparison, 'title' | 'description' | 'value' | 'color' | 'url' | 'type'> & {id: string}

export namespace QuestionResultsLayout {
  /**
   * All of the resolutions (including different at different times) for a question or subquestion.
   *  Aliasing the type makes it much easier to work with because the forecast type is huge
   */
  type TsResolutions = TsMd & {
    key: string
    resolutions: ResolutionForecasts<ForecastFetchedBase, UseForecastGroup>[]
  }

  export type Props = {
    question: QuestionResultsQuestion
    getForecasts: (group: ForecastGroupKey) => UseForecastGroup | undefined
    timestamps?: Array<TsMd>
    subqs: SubqFetched[]
    show?: ResultsQuery.ResultShowType[]
    showQuestions?: string[]
    subquestionHighlightId?: string
    forecasters?: string[]
    resultsColor?: (type: QuestionColor) => string
  }

  export type TsMd = {ts: number | undefined; tsName?: string}

  export const layout = ({
    question,
    subqs: rawSubqs,
    resultsColor,
    show,
    showQuestions: questionFilter,
    subquestionHighlightId,
    forecasters: forecastersFilter,
    ...props
  }: Props) => {
    /** wraps the get forecast to only include visible */
    const getForecasts = (group: ForecastGroupKey): UseForecastGroup | undefined =>
      doSafe(props.getForecasts(group), ({forecasts, ...r}) => ({
        ...r,
        forecasts: forecasts?.filter(
          f =>
            F.shouldShow(f) &&
            (isNullOrEmpty(forecastersFilter) || (f.created_by && forecastersFilter.includes(f.created_by)))
        )
      }))

    const timestamps = (
      props.timestamps && !isEmpty(props.timestamps) ?
        props.timestamps
      : [{ts: undefined} as TsMd]).map(t => ({
      ...t,
      tsName: t.tsName ?? (t.ts == null ? 'Now' : fmtDate(DateTime.fromMillis(t.ts)))
    }))

    const getShow = (type: ResultsQuery.ResultShowType) =>
      isNullOrEmpty(show) ?
        ['question', 'cause', 'consequence', 'related', 'help'].includes(type) // if no show filters, then this list is the defaults
      : match(type)
          // looking for a subq type, and no subq types are filtered then show is true
          .with('question', 'cause', 'consequence', 'related', t =>
            pipe(
              show.filter(s => ['question', 'cause', 'consequence', 'related'].includes(s)),
              subqShows => isEmpty(subqShows) || subqShows.includes(t)
            )
          )
          .otherwise(s => show.includes(s))

    const shouldShowQuestion = (id: string) =>
      questionFilter == null || questionFilter.length == 0 || questionFilter.includes(id)

    const groupsRaw = Subqs.group(rawSubqs, {question})
    const groups = groupsRaw
      .map(({subqs, ...g}) => {
        const hideGroupQuestion = !shouldShowQuestion(g.id) || (g.isRootQuestion && question.disableQuestionForecast)
        const groupResolution =
          hideGroupQuestion ?
            []
          : (timestamps.map(t =>
              g.isRootQuestion ?
                {
                  ...t,
                  resolutions: [
                    F.getQuestionResolutionForecast({getForecasts: g => getForecasts({ts: t.ts, ...g}), question})
                  ]
                    .filter(notNull)
                    .map(r => ({
                      ...r,
                      resolution: {...r.resolution, color: resultsColor?.('question')}
                    })),
                  key: `question|q:${question.id}|ts:${t.ts}`
                }
              : {
                  ...t,
                  resolutions: F.getResolutionForecasts({
                    question,
                    subq: g.cfg,
                    getForecasts: g => getForecasts({ts: t.ts, ...g}),
                    sqId: g.id,
                    parentSubq: g.groupParentQ
                  }).filter(f => !isEmpty(f.forecasts) && f.resolution.enableForecasts) as ResolutionForecasts<
                    ForecastFetchedBase,
                    UseForecastGroup
                  >[]
                }
            ) as TsResolutions[])

        const subqResolutions = (
          subqs.filter(
            ({id, cfg: {status, subquestionType, childrenEnabled}}) =>
              getShow(subquestionType) &&
              ['approved', 'closed', 'resolved'].includes(status) &&
              shouldShowQuestion(id) &&
              !(subquestionType == 'related' && childrenEnabled) // we show these at the top of the group. so don't show it as a child as well
          ) ?? []
        ).map(s => ({
          ...s,
          tsResolutions: timestamps.map(t => ({
            ...t,
            key: `subq|q:${question.id}|sq:${s.id}|ts:${t.ts}`,
            resolutions: F.getResolutionForecasts({
              question,
              subq: s.cfg,
              getForecasts: g => getForecasts({ts: t.ts, ...g}),
              sqId: s.id,
              parentSubq: g.isRootQuestion ? undefined : g.cfg
            }).filter(f => !isEmpty(f.forecasts) && f.resolution.enableForecasts) as ResolutionForecasts<
              ForecastFetchedBase,
              UseForecastGroup
            >[]
          })) as TsResolutions[],

          highlighted: subquestionHighlightId == s.id
        }))
        return {...g, groupResolution, subqResolutions, hideGroupQuestion}
      })
      .filter(g => !isEmpty(g.groupResolution) || !isEmpty(g.subqResolutions))
    return {groups, timestamps, shouldShow: getShow}
  }

  export type Layout = ReturnType<typeof layout>
  export type LayoutGroup = Layout['groups'][number]
  export type LayoutSubqResolution = LayoutGroup['subqResolutions'][number]
  export type LayoutGroupResolution = LayoutGroup['groupResolution'][number]
}
